import React, { useContext, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { PdfGenerationContext } from "./PdfGenerationContext";
import { MobileBottomBarContext } from "./ReceivingWizardDialog";
import approvalreview from "../../../assets/images/approval-review.webp";

const defaultTitle = "Review Billing Report — PDF Preview";
const defaultDescription =
  "Approve to notify the customer if the PDF is correct. Re-open the receiving process to make changes.";
const defaultSubDescription =
  "The customer-facing PDF report should reflect exactly what was received in the warehouse.";

export default function BillingIntroStep({
  onStart,
  onApprove,
  isApproving,
  title = defaultTitle,
  description = defaultDescription,
  subDescription = defaultSubDescription,
  buttonText = "Start Reviewing",
  loadingText = "Generating PDF...",
}) {
  const isPdfGenerating = useContext(PdfGenerationContext);
  const mobileCtx = useContext(MobileBottomBarContext);
  const isMobile = mobileCtx?.isMobile;

  useEffect(() => {
    if (!isMobile || !mobileCtx) return;

    mobileCtx.setBottomActions(
      <>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={onStart}
          disabled={isPdfGenerating || isApproving}
          fullWidth
        >
          {isPdfGenerating ? loadingText : "Review & Edit"}
        </Button>
        {onApprove && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onApprove}
            disabled={isPdfGenerating || isApproving}
            fullWidth
          >
            {isApproving ? "Approving..." : "Approve & Notify"}
          </Button>
        )}
      </>
    );

    return () => mobileCtx.setBottomActions(null);
  }, [isMobile, isPdfGenerating, isApproving, onStart, onApprove, loadingText]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={6}
      px={4}
      textAlign="center"
      height="100%"
    >
      <Typography variant="h4" fontWeight={500} sx={{ mb: 4, color: "#000" }}>
        {title}
      </Typography>

      <Box
        component="img"
        src={approvalreview}
        alt="Review"
        sx={{
          maxWidth: 275,
          width: "100%",
          height: "auto",
          mb: 4,
        }}
      />

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 500, lineHeight: 1.6, fontSize: 17 }}
      >
        {description}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 4, maxWidth: 500, lineHeight: 1.5, fontSize: 15, fontStyle: "italic" }}
      >
        {subDescription}
      </Typography>

      <Box display="flex" gap={2} alignItems="center">
        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={onStart}
          disabled={isPdfGenerating || isApproving}
          sx={{ px: 4, py: 1 }}
        >
          {isPdfGenerating ? loadingText : "Review & Edit"}
        </Button>
        {onApprove && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onApprove}
            disabled={isPdfGenerating || isApproving}
            sx={{ px: 4, py: 1 }}
          >
            {isApproving ? "Approving..." : "Approve & Notify"}
          </Button>
        )}
      </Box>

      {isPdfGenerating && (
        <Typography variant="body1" sx={{ mt: 2, color: "#d32f2f", maxWidth: 500, fontSize: 16 }}>
          We’re preparing the PDF report. You can review and approve it as soon as it’s ready
        </Typography>
      )}
    </Box>
  );
}
